/**
 * API Configuration Module
 *
 * This module stores runtime configuration for the API client.
 * The consuming app (hub or hub-next) must call configureApiClient()
 * during initialization to provide the API endpoint.
 */

interface ApiConfig {
  baseUrl: string | null;
}

const config: ApiConfig = {
  baseUrl: null,
};

/**
 * Configure the API client with the base URL.
 * This must be called by the consuming app during initialization.
 *
 * @param baseUrl - The API endpoint URL (e.g., 'https://api.example.com')
 *
 * @example
 * // In hub app (Vite):
 * import { configureApiClient } from '@monorepo/hub-common';
 * import { EnvService } from './services/core/env-service';
 *
 * configureApiClient(EnvService.apiEndPoint);
 *
 * @example
 * // In hub-next app (Next.js):
 * import { configureApiClient } from '@monorepo/hub-common';
 *
 * configureApiClient(process.env.NEXT_PUBLIC_API_END_POINT!);
 */
export const configureApiClient = (baseUrl: string): void => {
  if (!baseUrl) {
    throw new Error(
      'API base URL is required. Please provide a valid API endpoint.',
    );
  }
  config.baseUrl = baseUrl;
};

/**
 * Get the configured API base URL.
 * Throws an error if the API client hasn't been configured yet.
 *
 * @internal
 */
export const getApiBaseUrl = (): string => {
  if (!config.baseUrl) {
    throw new Error(
      'API client not configured. Please call configureApiClient() with your API endpoint before using any API hooks or functions.',
    );
  }
  return config.baseUrl;
};

/**
 * Check if the API client has been configured.
 *
 * @internal
 */
export const isApiClientConfigured = (): boolean => {
  return config.baseUrl !== null;
};
